
import React from 'react';
import { AlertTriangle, Clock, ExternalLink } from 'lucide-react';

export function PriceDisplay({ 
  price, 
  priceOld, 
  lastUpdated, 
  stockStatus, 
  asin, 
  showWarning = true,
  size = 'normal' 
}) {
  const isStale = lastUpdated && Date.now() - new Date(lastUpdated) > 24 * 60 * 60 * 1000; // 24 hours
  const partnerTag = import.meta.env.VITE_AMAZON_PARTNER_TAG;
  const amazonUrl = `https://www.amazon.com/dp/${asin}?tag=${partnerTag}`;
  
  const formatTime = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Less than 1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffHours < 48) return '1 day ago';
    return `${Math.floor(diffHours / 24)} days ago`;
  };

  const priceClasses = {
    small: 'text-lg',
    normal: 'text-xl',
    large: 'text-2xl xl:text-3xl'
  };

  const oldPriceClasses = {
    small: 'text-sm',
    normal: 'text-sm',
    large: 'text-lg xl:text-xl'
  };

  return (
    <div className="space-y-2">
      {/* Main Price Display */}
      <div className="flex items-baseline gap-2">
        <span className={`${priceClasses[size]} font-bold text-gray-900 dark:text-gray-100`}>
          ${Number(price).toFixed(2)}
        </span>
        {priceOld && (
          <span className={`${oldPriceClasses[size]} text-gray-500 dark:text-gray-400 line-through`}>
            ${Number(priceOld).toFixed(2)}
          </span>
        )}
        
        {/* Warning Icon with Tooltip */}
        {showWarning && isStale && (
          <div className="group relative">
            <AlertTriangle 
              className="w-4 h-4 text-amber-500 cursor-help" 
              aria-label="Price may be outdated"
            />
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
              <div className="text-center">
                <div className="font-medium">⚠️ Price may be outdated</div>
                <div className="text-gray-300 mt-1">Please check Amazon for current price</div>
              </div>
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        )}
      </div>

      {/* Price Update Info */}
      {showWarning && lastUpdated && (
        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
          <Clock className="w-3 h-3" />
          <span>Last updated {formatTime(lastUpdated)}</span>
          {isStale && (
            <span className="text-amber-600 dark:text-amber-400 font-medium">
              - may be outdated
            </span>
          )}
        </div>
      )}

      {/* Stock Status Warning */}
      {showWarning && stockStatus !== undefined && (
        <div className="flex items-center gap-1 text-xs">
          <div className={`w-2 h-2 rounded-full ${stockStatus ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <span className={stockStatus ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
            {stockStatus ? 'In Stock' : 'Out of Stock'}
          </span>
          {isStale && (
            <span className="text-gray-500 dark:text-gray-400">
              (status may have changed)
            </span>
          )}
        </div>
      )}

      {/* Amazon Verification Link */}
      {showWarning && (
        <div className="pt-1">
          <button
            onClick={() => window.open(amazonUrl, '_blank', 'noopener,noreferrer')}
            className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            Verify current price on Amazon
          </button>
        </div>
      )}
    </div>
  );
}
